import React, { MutableRefObject } from 'react';
import { withLocalePrefix } from 'shared/utils/translation';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import styled from 'styled-components';
import { Chip } from '@grnhse/seedling/lib/birch/components/chips';
import makeStyles from '@mui/styles/makeStyles';
import TypographyTheme from '@grnhse/seedling/lib/birch/typography/theme';
import Theme from '@grnhse/seedling/lib/birch//components/forms/text_input/theme';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import textInputTheme from '@grnhse/seedling/lib/birch/components/forms/text_input/theme';

const t = withLocalePrefix(
  'talent_matching.modals.calibration.additional_details'
);

type Props = {
  onAdd: (keywords: string[]) => void;
  onRemove: (keyword: string) => void;
  values: string[];
  booleanIndicator?: string;
  inputRef: MutableRefObject<HTMLElement | null>;
  disabled?: boolean;
};

const useStyles = makeStyles({
  inputRoot: {
    '&&[class*="MuiOutlinedInput-root"]': {
      paddingLeft: '16px',
      paddingTop: '20px',
      paddingBottom: '8px',
      outline: 'none',
      '&.Mui-focused fieldset': {
        boxShadow: `inset 0px -2px 0px ${Theme.focused.borderBottomColor}`,
      },
      '& span': {
        margin: '4px 0px 4px 4px',
      },
      '& fieldset': {
        borderRadius: '4px 4px 0 0',
        borderColor: `${textInputTheme.default.borderColor}`,
      },
      '& input': {
        border: 'none',
      },
    },
  },
  inputWrapper: {
    '&&&&': {
      borderRadius: '4px',
      fontFamily: TypographyTheme.fonts.sans,
      fontSize: '1rem',
      lineHeight: '1.5rem',
      color: Theme.default.color,
      WebkitTextFillColor: Theme.default.color,
      '& input': {
        border: 'none',
      },
      '&&.MuiInputBase-multiline': {
        padding: 0,
      },
    },
  },
});

const CustomTextField = styled(TextField)`
  label {
    top: 8px;
    transform: none;
    padding-left: 16px;
    margin-bottom: 8px;
    font-family: ${TypographyTheme.fonts.sans};
    font-weight: ${TypographyTheme.fontWeights.medium};
    font-size: 0.8125rem;
    line-height: 1.125rem;
    overflow: hidden;
    width: calc(100% - 2rem);
    color: ${Theme.default.labelColor};
  }
  .MuiInputBase-root {
    min-height: 60px;

    .MuiOutlinedInput-notchedOutline legend {
      display: none;
    }
  }
  .Mui-focused {
    color: ${Theme.default.labelColor};
  }
  &&&& .MuiOutlinedInput-root {
    padding-top: 24px;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderTextField = (props: any, label: string) => {
  return (
    <CustomTextField
      {...props}
      fullWidth
      label={label}
      InputLabelProps={{
        shrink: true,
      }}
    />
  );
};

const WorkTitleField = ({
  onAdd,
  onRemove,
  values,
  booleanIndicator,
  inputRef,
  disabled = false,
}: Props) => {
  const classes = useStyles();

  const onAddItem = (keywords: string[]) => {
    const recentKeyword = keywords.at(-1) as string;

    if (
      keywords.length > values.length &&
      values
        .map((value) => value.toLowerCase())
        .includes(recentKeyword?.toLowerCase())
    ) {
      return;
    }

    onAdd(keywords);
  };

  return (
    <Autocomplete
      disabled={disabled}
      clearIcon={false}
      options={[]}
      freeSolo
      multiple
      fullWidth
      onChange={(e, keywords) => {
        e.preventDefault();
        onAddItem(keywords);
      }}
      value={values}
      classes={{
        inputRoot: classes.inputRoot,
        input: classes.inputWrapper,
      }}
      renderTags={(tags) => {
        return tags.map((option, index) => (
          <>
            <Chip
              text={option}
              key={index}
              onRemove={disabled ? undefined : () => onRemove(option)}
            />
            {booleanIndicator && index !== tags.length - 1 && (
              <StyledInidicator metadata>{booleanIndicator}</StyledInidicator>
            )}
          </>
        ));
      }}
      renderInput={(params) => {
        return renderTextField({ ...params, inputRef, disabled }, t('titles'));
      }}
    />
  );
};

const StyledInidicator = styled(SecondaryBody)`
  padding: 8px;
`;

export default WorkTitleField;
